import { isPlatformBrowser } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { take } from 'rxjs'

import { AuthService } from '../../../../services/auth/auth.service'
import { getArticleIdFromRoute } from '../../../auth/util/article-purchase-route.utils'
import { ArticlePurchaseAccountShellComponent } from '../article-purchase-account-shell.component'

@Component({
  selector: 'dhp-article-purchase-account-verify',
  templateUrl: './article-purchase-account-verify.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ArticlePurchaseAccountShellComponent],
})
export class ArticlePurchaseAccountVerifyComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID)
  private readonly destroyRef = inject(DestroyRef)
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly authService = inject(AuthService)
  private readonly navigationState = this.getNavigationState()

  readonly articleId = computed(() => getArticleIdFromRoute(this.route))
  readonly email = computed(
    () =>
      this.navigationState.email ??
      this.route.snapshot.queryParamMap.get('email')
  )
  readonly mode = computed<'create' | 'update'>(() =>
    this.route.snapshot.queryParamMap.get('mode') === 'update'
      ? 'update'
      : 'create'
  )
  readonly checkoutUrl = computed(() => {
    const articleId = this.articleId()

    return articleId !== null
      ? `/articles/${String(articleId)}/purchase`
      : '/articles'
  })

  ngOnInit(): void {
    this.authService
      .waitForAuthCheckCompletion()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.shouldRedirectToCheckout()) {
          void this.router.navigateByUrl(this.checkoutUrl(), {
            replaceUrl: true,
          })
          return
        }

        if (this.email()) {
          return
        }

        const articleId = this.articleId()

        void this.router.navigate(
          articleId !== null
            ? ['/articles', articleId, 'purchase', 'account', 'register']
            : ['/articles'],
          { replaceUrl: true }
        )
      })
  }

  readonly titleKey = computed(() =>
    this.mode() === 'update'
      ? 'pages.articlePurchase.accountVerify.updateTitle'
      : 'pages.articlePurchase.accountVerify.title'
  )
  readonly descriptionKey = computed(() =>
    this.mode() === 'update'
      ? 'pages.articlePurchase.accountVerify.updateDescription'
      : null
  )

  private getNavigationState(): { email?: string } {
    const currentNavigationState = this.router.currentNavigation()?.extras
      .state as { email?: string } | undefined

    if (currentNavigationState) {
      return currentNavigationState
    }

    if (isPlatformBrowser(this.platformId)) {
      return (window.history.state ?? {}) as { email?: string }
    }

    return {}
  }

  private shouldRedirectToCheckout(): boolean {
    const articleId = this.articleId()
    const currentUser = this.authService.currentUser()

    return (
      articleId !== null &&
      this.authService.isAuthenticated() &&
      !!currentUser &&
      currentUser.managed !== true &&
      currentUser._verified === true
    )
  }
}
